<script setup lang="ts">
import { computed } from 'vue';
import { HelpCircle, ChevronDown } from 'lucide-vue-next';

import useContext from '@/composables/useContext';
import useCountrySwitcher from '@/composables/useCountrySwitcher';
import useGiftCard from '@/composables/useGiftCard';
import useWebsiteTexts from '@/composables/useWebsiteTexts';

import Logout from '@/components/header/Logout.vue';
import LocaleSelector from '@/components/LocaleSelector.vue';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

defineProps<{
  inverted?: boolean;
}>();

const { channel, header } = useContext();
const { activeGiftCard } = useGiftCard();
const { availableCountries } = useCountrySwitcher();
const { websiteText } = useWebsiteTexts();

const hasActiveGiftCard = computed(() => (activeGiftCard.value?.giftCards?.length ?? 0) > 0);

const currentCountry = computed<any>(() => {
  const list = availableCountries.value as any[];
  if (!Array.isArray(list)) return null;
  // Match the country to the channel we're actually on (authoritative) rather
  // than the `current` flag, which can be stale right after a locale change.
  const channelSystemId = channel.value?.systemId;
  return (channelSystemId && list.find((c) => c.channelId === channelSystemId)) || list.find((c) => c.current) || list[0] || null;
});

const currentLanguage = computed(() => {
  const langs = currentCountry.value?.availableLanguages ?? [];
  return langs.find((l: any) => l.current) || langs[0] || null;
});

const countryDisplayName = computed(() => {
  const c = currentCountry.value;
  const fromChannel = channel.value?.displayName;
  const fromCountry = c?.displayName || c?.channelName || c?.id;
  const raw = fromCountry || fromChannel || '';
  return raw === '001' ? 'EU' : raw;
});

const languageDisplayName = computed(() => currentLanguage.value?.name || currentLanguage.value?.languageId || '');

const flagSrc = computed(() => {
  const id = currentCountry.value?.id;
  if (!id) return '';
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
    return require(`@/static/flags/${id.toLowerCase()}.svg`);
  } catch {
    return '';
  }
});

const topNavigationLink = computed(() => header.value?.topNavigatonLink);

const redeemUrl = computed(() => channel.value?.rootPath || '/');
</script>

<template>
  <div
    class="hidden px-4 w-full tablet-up:block"
    :class="inverted ? 'absolute inset-x-0 top-0 z-40 bg-transparent text-background' : 'bg-topbar text-topbar-foreground'"
  >
    <div class="relative mx-auto flex text-xs h-9 max-w-[var(--site-max-width)] items-center justify-between">
      <div class="flex items-center gap-4">
        <a
          v-if="topNavigationLink"
          :href="topNavigationLink.url"
          class="flex items-center gap-1 visited:text-inherit text-inherit hover:text-inherit no-underline hover:underline"
        >
          <HelpCircle class="h-[14px] w-[14px]" />
          <span>{{ topNavigationLink.label }}</span>
        </a>
      </div>

      <div class="flex items-center gap-4">
        <Popover>
          <PopoverTrigger as-child>
            <button type="button" class="btn-bare inline-flex items-center gap-1 text-inherit hover:underline" aria-label="Change country and language">
              <span v-if="flagSrc" class="inline-flex h-[14px] w-[14px] shrink-0 overflow-hidden rounded-full">
                <img :src="flagSrc" :alt="countryDisplayName" class="h-full w-full object-cover" />
              </span>
              <span>
                {{ countryDisplayName }}<template v-if="languageDisplayName">, {{ languageDisplayName }}</template>
              </span>
              <ChevronDown class="h-3 w-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" :side-offset="8" :collision-padding="16" class="z-[10000] w-80 border border-border bg-background p-5 text-foreground">
            <LocaleSelector />
          </PopoverContent>
        </Popover>

        <Logout v-if="hasActiveGiftCard" class="cursor-pointer text-inherit hover:underline" />
        <a v-else-if="!inverted" :href="redeemUrl" class="text-inherit visited:text-inherit no-underline hover:underline">
          {{ websiteText('header__redeem_giftcard').value || 'Lös in gåvokort' }}
        </a>
      </div>
    </div>
  </div>
</template>
